import React, { useCallback, useEffect, useState } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

const formatDateTime = (value) => {
  if (!value) {
    return 'N/A'
  }

  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const TransferHistory = () => {
  const [transfers, setTransfers] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  const fetchTransfers = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMessage('')

      const response = await apiClient.get(API_ENDPOINTS.STOCK_TRANSFERS)
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      
      // Filter out transfers with status "pending"
      const completedTransfers = data.filter(transfer => transfer.status !== 'pending')
      
      setTransfers(completedTransfers)
    } catch (error) {
      console.error('Error fetching transfers:', error)
      setErrorMessage(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to load transfer history. Please try again.'
      )
      setTransfers([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTransfers()
  }, [fetchTransfers])

  const toggleRow = (transferId) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(transferId)) {
      newExpanded.delete(transferId)
    } else {
      newExpanded.add(transferId)
    }
    setExpandedRows(newExpanded)
  }

  const filteredTransfers = transfers.filter(transfer => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    return (
      transfer.reference_number?.toLowerCase().includes(searchLower) ||
      transfer.transfer_type_display?.toLowerCase().includes(searchLower) ||
      transfer.source_warehouse_name?.toLowerCase().includes(searchLower) ||
      transfer.source_branch?.toLowerCase().includes(searchLower) ||
      transfer.destination_warehouse_name?.toLowerCase().includes(searchLower) ||
      transfer.destination_branch_name?.toLowerCase().includes(searchLower) ||
      transfer.status_display?.toLowerCase().includes(searchLower)
    )
  })

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success'
      case 'pending':
        return 'bg-warning'
      case 'cancelled':
        return 'bg-danger'
      default:
        return 'bg-secondary'
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Transfer History</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Stock Transfers</li>
            <li className="breadcrumb-item active">Transfer History</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">Transfer History</h5>
                  <button
                    className="btn btn-primary"
                    onClick={fetchTransfers}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh
                  </button>
                </div>

                {errorMessage && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {errorMessage}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setErrorMessage('')}
                    ></button>
                  </div>
                )}

                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by reference, type, source, destination, or status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading transfer history...</p>
                  </div>
                ) : filteredTransfers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted"></i>
                    <p className="mt-3 text-muted">
                      {searchTerm ? 'No transfers found matching your search.' : 'No completed transfers found.'}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <th>Reference</th>
                          <th>Type</th>
                          <th>Source</th>
                          <th>Destination</th>
                          <th>Items</th>
                          <th>Total Cost</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransfers.map((transfer) => (
                          <React.Fragment key={transfer.id}>
                            <tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleRow(transfer.id)}
                            >
                              <td>
                                <i
                                  className={`bi bi-chevron-${expandedRows.has(transfer.id) ? 'down' : 'right'}`}
                                ></i>
                              </td>
                              <td>
                                <strong>{transfer.reference_number}</strong>
                              </td>
                              <td>{transfer.transfer_type_display}</td>
                              <td>
                                {transfer.source_warehouse_name || transfer.source_branch || 'N/A'}
                              </td>
                              <td>
                                {transfer.destination_warehouse_name ||
                                  transfer.destination_branch_name ||
                                  'N/A'}
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {transfer.item_count || transfer.items?.length || 0}
                                </span>
                              </td>
                              <td>
                                <strong>
                                  {currencyFormatter.format(transfer.total_cost || 0)}
                                </strong>
                              </td>
                              <td>
                                <span
                                  className={`badge ${getStatusBadgeClass(transfer.status)}`}
                                >
                                  {transfer.status_display}
                                </span>
                              </td>
                              <td>
                                <small>{formatDateTime(transfer.created_at)}</small>
                                <br />
                                <small className="text-muted">
                                  by {transfer.created_by_email}
                                </small>
                              </td>
                              <td>
                                {transfer.completed_at ? (
                                  <>
                                    <small>{formatDateTime(transfer.completed_at)}</small>
                                    <br />
                                    <small className="text-muted">
                                      by {transfer.completed_by_email}
                                    </small>
                                  </>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(transfer.id) && (
                              <tr>
                                <td colSpan="10" className="bg-light">
                                  <div className="p-4">
                                    <h6 className="mb-3">Transfer Details</h6>
                                    <div className="row mb-3">
                                      <div className="col-md-6">
                                        <small className="text-muted d-block">Notes</small>
                                        <p className="mb-0">
                                          {transfer.notes || <em className="text-muted">No notes</em>}
                                        </p>
                                      </div>
                                      <div className="col-md-6">
                                        <small className="text-muted d-block">Batch Number</small>
                                        <p className="mb-0">
                                          {transfer.batch_number || <em className="text-muted">N/A</em>}
                                        </p>
                                      </div>
                                    </div>

                                    <h6 className="mb-3 mt-4">Transfer Items</h6>
                                    {transfer.items && transfer.items.length > 0 ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                          <thead className="table-secondary">
                                            <tr>
                                              <th>Product</th>
                                              <th>SKU</th>
                                              <th>Supplier</th>
                                              <th>Quantity</th>
                                              <th>Purchase Price</th>
                                              <th>Selling Price</th>
                                              <th>Total Cost</th>
                                              <th>Batch Number</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {transfer.items.map((item) => (
                                              <tr key={item.id}>
                                                <td>{item.product_name}</td>
                                                <td>
                                                  <code>{item.product_sku}</code>
                                                </td>
                                                <td>{item.supplier_name}</td>
                                                <td>{item.quantity}</td>
                                                <td>
                                                  {currencyFormatter.format(
                                                    Number(item.purchase_price) || 0
                                                  )}
                                                </td>
                                                <td>
                                                  {item.selling_price
                                                    ? currencyFormatter.format(
                                                        Number(item.selling_price) || 0
                                                      )
                                                    : 'N/A'}
                                                </td>
                                                <td>
                                                  <strong>
                                                    {currencyFormatter.format(
                                                      Number(item.total_cost) || 0
                                                    )}
                                                  </strong>
                                                </td>
                                                <td>
                                                  {item.batch_number || (
                                                    <em className="text-muted">N/A</em>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <p className="text-muted">No items found</p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default TransferHistory

